/**
 * Projects P1 (engineer teammates, spec mockups/engineer-teammates-spec.html §4) — the wire
 * contract between the runner that finds a box's projects and the server that keeps them.
 *
 * A project is a folder under files/ that holds a repository or an app: every folder in
 * files/apps/, and every folder the files walk judges a project root (user-files-scope.ts).
 * The runner reports them on the lease beat (SANDBOX_PROJECTS_V1) only when they changed, and the
 * server writes the report into `projects` (migration 20270113090000_projects.sql).
 *
 * Both halves read a report through `readProjectsReport`, so what the runner sends and what
 * the server accepts are the same rule. The server strips credentials from a remote AGAIN:
 * no credential is ever stored, whatever a runner sends.
 */
import { z } from 'zod';
import type { AgentFace } from '../team/types.js';
import type { TaskLifecycleState } from '../types/task.js';

/** At most this many projects ride one beat; past it the report is marked incomplete. */
export const MAX_PROJECTS_PER_REPORT = 50;
/** A dirty count is counted up to this and reported as this: "this many or more". */
export const DIRTY_COUNT_CAP = 1000;

/** The recent saves (commits) one project reports, newest first. */
export const MAX_RECENT_SAVES = 5;
/** The top-level entries one project reports. */
export const MAX_TOP_LEVEL_ENTRIES = 40;

const count = z.number().int().min(0);

/**
 * Text Postgres can keep in jsonb: no NUL, and no half of a surrogate pair (a string cut
 * mid-pair). Either one fails the whole report's write, every project with it.
 */
export function isStorableText(text: string): boolean {
  return !/\u0000|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/.test(text);
}
const text = (min: number, max: number) => z.string().min(min).max(max).refine(isStorableText);

/** One save (commit): when it landed (its committer time, ISO), who made it, the first line of its message. */
export const ProjectSaveSchema = z.object({
  at: z.string().datetime(),
  author: text(0, 100),
  message: text(0, 200),
});
export type ProjectSave = z.infer<typeof ProjectSaveSchema>;

/** One entry at the top of the project's folder. */
export const ProjectEntrySchema = z.object({
  name: text(1, 200),
  dir: z.boolean(),
});
export type ProjectEntry = z.infer<typeof ProjectEntrySchema>;

/** One project as the runner reports it. `path` is relative to the workspace (files/apps/acme). */
export const ProjectReportSchema = z.object({
  path: z.string().min(1).max(512),
  name: z.string().min(1).max(200),
  remoteUrl: z.string().max(2048).nullable().optional(),
  branch: z.string().min(1).max(255).nullable().optional(),
  headSha: z.string().regex(/^[0-9a-f]{40}([0-9a-f]{24})?$/).nullable().optional(),
  dirtyCount: count.max(DIRTY_COUNT_CAP).nullable().optional(),
  ahead: count.nullable().optional(),
  behind: count.nullable().optional(),
  bytes: count.nullable().optional(),
  stack: z.array(z.string().min(1).max(32)).max(16).optional(),
  /** HEAD's committer time, ISO (`toISOString`). */
  lastCommitAt: z.string().datetime().nullable().optional(),
  /** The last saves, newest first. Null with no git, no commit, or a repo too slow to read. */
  recentSaves: z.array(ProjectSaveSchema).max(MAX_RECENT_SAVES).nullable().optional(),
  /** The folder's top level: dirs first, then files, by name; no dot-names, no node_modules. */
  topLevel: z.array(ProjectEntrySchema).max(MAX_TOP_LEVEL_ENTRIES).nullable().optional(),
});
export type ProjectReport = z.infer<typeof ProjectReportSchema>;

export interface ProjectsReport {
  projects: ProjectReport[];
  /** True only when `projects` names every project on the box: the server marks the rest removed. */
  complete: boolean;
}

/**
 * A project's place on the box: under files/, at least one folder deep, every segment a real
 * name (no empty, `.` or `..` segment, so no leading, trailing or doubled slash), no control
 * characters. The table's CHECK says the same.
 */
export function isProjectBoxPath(path: string): boolean {
  if (path.length > 512 || !path.startsWith('files/') || /[\u0000-\u001f\u007f]/.test(path)) return false;
  return path.split('/').every((segment) => segment.length > 0 && segment !== '.' && segment !== '..');
}

/**
 * A git remote with any credentials removed, or null when it cannot be made safe.
 *   https://user:token@github.com/o/r → https://github.com/o/r (any URL: userinfo, query and
 *     fragment go — a token can sit in any of them);
 *   ssh://git@github.com/o/r.git and git@github.com:o/r.git keep the login `git`, which is
 *     the same for everyone; any other login goes too, since a token pasted as one is
 *     indistinguishable from a name;
 *   a local path is kept unless it holds an `@`.
 * It is the remote as an identity (which repository), not a working URL to push with.
 */
export function stripRemoteCredentials(raw: string): string | null {
  const url = raw.trim();
  if (url.length === 0 || url.length > 2048 || /[\s\u0000-\u001f\u007f]/.test(url)) return null;
  let out: string;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(url)) {
    let parsed: URL;
    try {
      parsed = new URL(url);
    } catch {
      return null;
    }
    const keepGit = parsed.username === 'git' && /ssh/i.test(parsed.protocol);
    parsed.password = '';
    if (!keepGit) parsed.username = '';
    parsed.search = '';
    parsed.hash = '';
    out = parsed.toString();
  } else {
    // scp-like `[user@]host:path` (no slash before the colon), or a local path.
    const scp = /^(?:([^@/:]+)@)?([^@/:]+):(?!\/\/)(.+)$/.exec(url);
    if (scp) {
      const [, user, host, path] = scp;
      if (path.includes('@')) return null;
      out = `${user === 'git' ? 'git@' : ''}${host}:${path}`;
    } else {
      if (url.includes('@')) return null;
      out = url;
    }
  }
  return out.length <= 512 ? out : null;
}

/**
 * Read a beat's `projects` + `projectsComplete`. Null when there is no list. An item that
 * does not parse, or whose path is not a project path, is dropped, and the report is then
 * INCOMPLETE: what it no longer lists might be the item we threw away, so it must not mark
 * anything removed. Same for a list past the cap, and for a missing or false
 * `projectsComplete`. A second item at the same path is dropped (the first wins).
 */
export function readProjectsReport(items: unknown, complete: unknown): ProjectsReport | null {
  if (!Array.isArray(items)) return null;
  let whole = complete === true && items.length <= MAX_PROJECTS_PER_REPORT;
  const projects: ProjectReport[] = [];
  const paths = new Set<string>();
  for (const raw of items.slice(0, MAX_PROJECTS_PER_REPORT)) {
    const parsed = ProjectReportSchema.safeParse(raw);
    if (!parsed.success || !isProjectBoxPath(parsed.data.path)) {
      whole = false;
      continue;
    }
    if (paths.has(parsed.data.path)) continue;
    paths.add(parsed.data.path);
    const { remoteUrl } = parsed.data;
    projects.push({ ...parsed.data, remoteUrl: remoteUrl ? stripRemoteCredentials(remoteUrl) : null });
  }
  return { projects, complete: whole };
}

/**
 * The framework a project is, for its folder badge: the most specific hint wins (a Next.js app
 * also lists react and node). Null when no hint names one (a folder of docker and supabase).
 */
export const PRIMARY_STACK_ORDER = [
  'nextjs', 'astro', 'remix', 'svelte', 'vue', 'react', 'vite', 'express',
  'python', 'go', 'rust', 'java', 'ruby', 'php', 'node',
] as const;

export function primaryStack(stack: readonly string[] | null | undefined): string | null {
  if (!stack) return null;
  return PRIMARY_STACK_ORDER.find((hint) => stack.includes(hint)) ?? null;
}

/**
 * "owner/name" from a remote (https://github.com/o/r.git, git@github.com:o/r.git,
 * ssh://git@host/o/r), for the card's repo line. Null for a local path or anything that
 * does not end in two path segments.
 */
export function repoLabel(remoteUrl: string | null | undefined): string | null {
  if (!remoteUrl) return null;
  let path: string;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(remoteUrl)) {
    try {
      const url = new URL(remoteUrl);
      if (url.protocol === 'file:' || !url.hostname) return null;
      path = url.pathname;
    } catch {
      return null;
    }
  } else {
    const scp = /^(?:[^@/:]+@)?[^@/:]+:(?!\/\/)(.+)$/.exec(remoteUrl);
    if (!scp) return null;
    path = scp[1];
  }
  const segments = path.replace(/\.git$/, '').split('/').filter(Boolean);
  if (segments.length < 2) return null;
  const label = segments.slice(-2).join('/');
  return /^[\w.-]+\/[\w.-]+$/.test(label) ? label : null;
}

/** The app a project runs: a task_apps row, keyed by its port (open it with POST /api/apps/:port/wake). */
export interface ProjectApp {
  port: number;
  task_id: string;
  name: string | null;
  caption: string | null;
  path: string | null;
  announced_at: string;
}

/** Who built a project: the teammate on its most recent task, as TeamAgent shows them. */
export interface ProjectBuilder {
  id: string;
  handle: string;
  name: string;
  kind: 'agent' | 'lead';
  face: AgentFace;
  /** kinIndexFor(id), 0..19; -1 for the lead. */
  kin: number;
}

/** A project as GET /api/projects answers it. */
export interface Project {
  id: string;
  name: string;
  box_path: string;
  remote_url: string | null;
  /** repoLabel(remote_url). */
  repo_label: string | null;
  branch: string | null;
  head_sha: string | null;
  /** HEAD's committer time: when the last save landed. */
  last_commit_at: string | null;
  dirty_count: number | null;
  ahead: number | null;
  behind: number | null;
  bytes: number | null;
  stack: string[] | null;
  /** primaryStack(stack): the folder badge. */
  primary_stack: string | null;
  first_seen_at: string;
  last_seen_at: string;
  removed_at: string | null;
  /** The last saves, newest first; null when the box has not read them (no git, an older runner). */
  recent_saves: ProjectSave[] | null;
  /** The folder's top level, dirs first; null when the box has not read it. */
  top_level: ProjectEntry[] | null;
  /** Its most recently announced running app, or null. */
  app: ProjectApp | null;
  /** The teammate on its most recent linked task, or null (the web shows Bash). */
  built_by: ProjectBuilder | null;
}

/**
 * A task that worked on a project (one of its apps' tasks, task_apps.project_id), as
 * GET /api/projects/:id answers it beside the project: the newest 10, a deleted task never.
 */
export interface ProjectTask {
  id: string;
  title: string | null;
  /** tasks.lifecycle_state: 'processing' is working now. */
  status: TaskLifecycleState | null;
  /** When it last did something (tasks.last_activity_at). */
  updated_at: string;
  /** The teammate on it, by the same rule as Project.built_by; null (the web shows Bash). */
  built_by: ProjectBuilder | null;
}
